import type { Category, ModifierAttachments, ModifierGroup, Product } from '@/types'
import { isSizeGroup } from './variantShape'

/**
 * The menu matrix's state and diff (F-MOD-N12), kept pure so the cycling
 * rule and the save plan can be tested without a DOM.
 *
 * A cell is one (product, group). It shows as:
 *   direct     on, attached to the item itself
 *   inherited  on, through the category
 *   excluded   off, the category asks it but this item opted out
 *   off        off
 *
 * Clicking cycles it the way an owner expects: on ↔ off. Under the hood the
 * write depends on whether the category pushes the group down.
 */

export type CellState = 'direct' | 'inherited' | 'excluded' | 'off'

export interface MatrixDraft {
  /** category id → group ids (ordered). */
  categories: Record<string, string[]>
  /** product id → { direct, excluded }. */
  products: Record<string, { direct: string[]; excluded: string[] }>
}

export function draftFromAttachments(a: ModifierAttachments | undefined): MatrixDraft {
  return {
    categories: { ...(a?.categories ?? {}) },
    products: Object.fromEntries(
      Object.entries(a?.products ?? {}).map(([id, v]) => [id, { direct: [...v.direct], excluded: [...v.excluded] }]),
    ),
  }
}

export function cellState(d: MatrixDraft, productId: string, categoryId: string | undefined, groupId: string): CellState {
  const p = d.products[productId]
  if (p?.direct.includes(groupId)) return 'direct'
  const catOn = Boolean(categoryId && d.categories[categoryId]?.includes(groupId))
  if (!catOn) return 'off'
  return p?.excluded.includes(groupId) ? 'excluded' : 'inherited'
}

export function isOn(s: CellState): boolean {
  return s === 'direct' || s === 'inherited'
}

/** Toggle one item cell. Returns a new draft. */
export function toggleCell(d: MatrixDraft, productId: string, categoryId: string | undefined, groupId: string): MatrixDraft {
  const cur = cellState(d, productId, categoryId, groupId)
  const p = d.products[productId] ?? { direct: [], excluded: [] }
  const next = { direct: [...p.direct], excluded: [...p.excluded] }
  switch (cur) {
    case 'direct':
      next.direct = next.direct.filter((x) => x !== groupId)
      // If the category also pushes it, turning it off means opting out.
      if (categoryId && d.categories[categoryId]?.includes(groupId) && !next.excluded.includes(groupId)) next.excluded.push(groupId)
      break
    case 'inherited':
      next.excluded.push(groupId)
      break
    case 'excluded':
      next.excluded = next.excluded.filter((x) => x !== groupId)
      break
    case 'off':
      next.direct.push(groupId)
      break
  }
  return { ...d, products: { ...d.products, [productId]: next } }
}

/** Toggle a category cell. Opt-outs on its items are kept as they were. */
export function toggleCategory(d: MatrixDraft, categoryId: string, groupId: string): MatrixDraft {
  const cur = d.categories[categoryId] ?? []
  const next = cur.includes(groupId) ? cur.filter((x) => x !== groupId) : [...cur, groupId]
  return { ...d, categories: { ...d.categories, [categoryId]: next } }
}

/** Groups shown as columns: everything except an item's private size list. */
export function matrixColumns(groups: ModifierGroup[], a: ModifierAttachments | undefined): ModifierGroup[] {
  const directCount = new Map<string, number>()
  const inCategory = new Set<string>()
  for (const v of Object.values(a?.products ?? {})) for (const g of v.direct) directCount.set(g, (directCount.get(g) ?? 0) + 1)
  for (const ids of Object.values(a?.categories ?? {})) for (const g of ids) inCategory.add(g)
  return groups.filter((g) => {
    if (!g.is_active) return false
    if (!isSizeGroup(g)) return true
    // A size list attached to exactly one item and no category is that
    // item's own; it lives in the Sizes column, not as a column of its own.
    return inCategory.has(g.id) || (directCount.get(g.id) ?? 0) !== 1
  })
}

/** The size list an item resolves to, if any (direct wins over category). */
export function sizeGroupFor(
  d: MatrixDraft,
  product: Pick<Product, 'id' | 'category_id'>,
  byId: Map<string, ModifierGroup>,
): ModifierGroup | null {
  const p = d.products[product.id]
  for (const id of p?.direct ?? []) {
    const g = byId.get(id)
    if (g && isSizeGroup(g)) return g
  }
  const cat = product.category_id ? d.categories[product.category_id] ?? [] : []
  for (const id of cat) {
    const g = byId.get(id)
    if (g && isSizeGroup(g) && !(p?.excluded ?? []).includes(id)) return g
  }
  return null
}

export interface SavePlan {
  categories: Array<{ id: string; groupIds: string[] }>
  products: Array<{ id: string; direct: string[]; excluded: string[] }>
}

const same = (a: string[] = [], b: string[] = []) => a.length === b.length && a.every((x, i) => x === b[i])
const sameSet = (a: string[] = [], b: string[] = []) => a.length === b.length && a.every((x) => b.includes(x))

/** What changed between the server's attachments and the draft. */
export function planSave(base: MatrixDraft, d: MatrixDraft): SavePlan {
  const plan: SavePlan = { categories: [], products: [] }
  const catIds = new Set([...Object.keys(base.categories), ...Object.keys(d.categories)])
  for (const id of catIds) {
    if (!same(base.categories[id], d.categories[id])) plan.categories.push({ id, groupIds: d.categories[id] ?? [] })
  }
  const prodIds = new Set([...Object.keys(base.products), ...Object.keys(d.products)])
  for (const id of prodIds) {
    const b = base.products[id] ?? { direct: [], excluded: [] }
    const n = d.products[id] ?? { direct: [], excluded: [] }
    if (!same(b.direct, n.direct) || !sameSet(b.excluded, n.excluded)) plan.products.push({ id, direct: n.direct, excluded: n.excluded })
  }
  return plan
}

/** Products grouped under their category, in category order; uncategorised last. */
export function groupProducts(products: Product[], categories: Category[]): Array<{ category: Category | null; products: Product[] }> {
  const byCat = new Map<string, Product[]>()
  const loose: Product[] = []
  for (const p of products) {
    if (p.category_id && categories.some((c) => c.id === p.category_id)) {
      const list = byCat.get(p.category_id) ?? []
      list.push(p)
      byCat.set(p.category_id, list)
    } else loose.push(p)
  }
  const out = categories
    .filter((c) => byCat.has(c.id))
    .map((c) => ({ category: c as Category | null, products: (byCat.get(c.id) ?? []).sort((a, b) => a.name.localeCompare(b.name)) }))
  if (loose.length) out.push({ category: null, products: loose.sort((a, b) => a.name.localeCompare(b.name)) })
  return out
}
